import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { Eye, EyeOff, Lock, Mail, User, Key, WifiOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [claveAdmin, setClaveAdmin] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar si hay conexión a internet
    if (!isOnline) {
      toast({
        title: "Sin conexión a internet",
        description: "Necesitas conexión a internet para iniciar sesión. Si ya iniciaste sesión antes, la app se cargará automáticamente.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);

    if (isSignUp) {
      // Validar clave de administrador
      if (claveAdmin !== "luz_verde") {
        toast({
          title: "Acceso denegado",
          description: "Clave de administrador incorrecta",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const { error } = await signUp(email, password, nombre);
      if (!error) {
        setTimeout(() => {
          navigate("/home");
        }, 1000);
      }
    } else {
      const { error } = await signIn(email, password);
      if (!error) {
        setTimeout(() => {
          navigate("/home");
        }, 500);
      }
    }

    setIsLoading(false);
  };

  return (
    <Card className="w-full max-w-md border border-border bg-white shadow-lg text-gray-900">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center text-gray-900">
          {isSignUp ? "Registrarse" : "Iniciar Sesión"}
        </CardTitle>
        <CardDescription className="text-center text-gray-900 font-medium">
          {isSignUp
            ? "Crea una nueva cuenta para acceder al sistema"
            : "Ingresa tus credenciales para acceder al sistema"
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off" spellCheck={false}>
          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-gray-900">Nombre</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="nombre"
                  type="text"
                  placeholder="Ingresa tu nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                  autoComplete="off"
                  required={isSignUp}
                />
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-900">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="Ingresa tu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                autoComplete="off"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-900">Contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                autoComplete="off"
                name="no-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-gray-900 transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="claveAdmin" className="text-gray-900">Clave de Administrador</Label>
              <div className="relative">
                <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="claveAdmin"
                  type="password"
                  placeholder="Ingresa la clave de administrador"
                  value={claveAdmin}
                  onChange={(e) => setClaveAdmin(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                  autoComplete="off"
                  required={isSignUp}
                />
              </div>
            </div>
          )}

          {!isOnline && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              <WifiOff className="h-4 w-4" />
              <span>Sin conexión a internet</span>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
            disabled={isLoading || !isOnline}
          >
            {isLoading 
              ? (isSignUp ? "Creando cuenta..." : "Iniciando sesión...") 
              : (isSignUp ? "Crear Cuenta" : "Iniciar Sesión")
            }
          </Button>
        </form>

        <div className="text-center">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsSignUp(!isSignUp)}
            disabled={isLoading}
          >
            {isSignUp 
              ? "¿Ya tienes cuenta? Inicia sesión" 
              : "¿No tienes cuenta? Regístrate"
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
